const CHECKOUT_URL = 'https://checkout.razorpay.com/v1/checkout.js'

let scriptPromise: Promise<void> | null = null

// Loads checkout.js exactly once regardless of how many callers ask concurrently.
function loadScript(): Promise<void> {
  if ((window as any).Razorpay) return Promise.resolve()
  if (scriptPromise) return scriptPromise
  scriptPromise = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script')
    s.src     = CHECKOUT_URL
    s.onload  = () => resolve()
    s.onerror = () => { scriptPromise = null; reject(new Error('Failed to load payment SDK')) }
    document.head.appendChild(s)
  })
  return scriptPromise
}

export interface RazorpayCheckoutOptions {
  keyId:       string
  orderId:     string
  amount:      number
  currency:    string
  name?:       string
  description?: string
}

export interface RazorpayPaymentResult {
  razorpayPaymentId: string
  razorpayOrderId:   string
  razorpaySignature: string
}

/**
 * Load the Razorpay checkout SDK (once) and open the payment modal.
 *
 * Resolves with the payment credentials on success.
 * Rejects with `new Error('cancelled')` when the user dismisses the modal,
 * or with a descriptive error when the payment itself fails.
 */
export async function openRazorpayCheckout(opts: RazorpayCheckoutOptions): Promise<RazorpayPaymentResult> {
  await loadScript()

  return new Promise<RazorpayPaymentResult>((resolve, reject) => {
    const rzp = new (window as any).Razorpay({
      key:         opts.keyId,
      amount:      opts.amount,
      currency:    opts.currency,
      order_id:    opts.orderId,
      name:        opts.name        ?? 'Ekkam',
      description: opts.description ?? 'Complete payment',
      theme:       { color: '#7c3aed' },
      handler: (r: { razorpay_payment_id: string; razorpay_order_id: string; razorpay_signature: string }) => {
        resolve({
          razorpayPaymentId: r.razorpay_payment_id,
          razorpayOrderId:   r.razorpay_order_id,
          razorpaySignature: r.razorpay_signature,
        })
      },
      modal: { ondismiss: () => reject(new Error('cancelled')) },
    })
    rzp.on('payment.failed', (r: any) => reject(new Error(r.error?.description ?? 'Payment failed')))
    rzp.open()
  })
}
